import React, { useEffect, useState } from "react";
import { setJourneyToken } from "@/lib/journeyToken";
import Div100vh from "react-div-100vh";
import { useRecoilState } from "recoil";
import { useNavigate } from "react-router-dom";

import { getDataB2B } from "@/lib/helper";
import {
  fetchJourneyWithGates,
  evaluateGate,
  GATE_DECISION,
} from "@/b2b/lib/journeyGates";
import {
  partnerNavigationRules,
  PARTNERS_WITHOUT_ZERO_DEPOSIT_MESSAGING,
} from "@/b2b/config/partnerJourney";
import { rentDetailsFormDataAtom } from "@/recoil/atom/rentDetailsFormDataAtom";
import cBg from "@/assets/images/congrats-ng.png";
import cGif from "@/assets/images/congrats.gif";
import PrimaryButton from "../components/PrimaryButton";
import { SUPPORT_TEL_HREF } from "@/b2b/config/support";

export default function CongratsScreen({ theme, allowButton = true }) {
  const navigate = useNavigate();
  const [formData, setFormData] = useRecoilState(rentDetailsFormDataAtom);
  const [journeyStatus, setJourneyStatus] = useState("LOCKED");
  // Congrats must only be shown AFTER banking actually succeeds. Keep the screen
  // hidden until we have verified the persisted BANKING step, so it never flashes for
  // a tenant whose banking is still in progress / under review (#15).
  const [verifyingBanking, setVerifyingBanking] = useState(true);
  const userIdentifier = localStorage.getItem("user_identifier");
  const partner = localStorage.getItem("partnerIdentifier");

  const blockPartners = ["hw", "meridianstays", "nestaway"];

  const handleClick = () => {
    // Dashboard-added partners aren't in partnerNavigationRules. Falling back to
    // "/onboarding" skips the Rent Summary screen — the only place that sets
    // rentSummaryConsent — so OnboardingSteps bounces the user straight back to
    // congrats forever. Every hardcoded partner's `congrats` step is "rent-summary";
    // use that as the safe default so new partners break out of the loop too.
    const nextStep =
      partnerNavigationRules?.[partner]?.congrats || "rent-summary";
    navigate(`/b/${userIdentifier}?partner=${partner}&step=${nextStep}`);
  };

  useEffect(() => {
    if (partner === "kots") {
      const nextStep =
        partnerNavigationRules?.[partner]?.congrats || "rent-summary";
      navigate(`/b/${userIdentifier}?partner=${partner}&step=${nextStep}`, {
        replace: true,
      });
    }
  }, []);

  useEffect(() => {
    const startOnboardingJourney = async () => {
      try {
        const result = await getDataB2B("b2b/onboard/start", userIdentifier);
        setJourneyToken(result?.token);
        setFormData(result?.user);
      } catch (error) {
        console.error("Error fetching data", error);
      }

      // Ask the SERVER whether banking genuinely completed, instead of reading the step
      // ourselves.
      //
      // The previous version of this check read `steps[BANKING].status === 'SUCCESS'`
      // directly. That was correct code reading a corrupt value: the step is written by 13
      // places in cpcore, none of which verify the webhook belongs to the tenant's CURRENT
      // banking attempt, so a late approval for attempt #1 marked it SUCCESS while attempt #3
      // had already failed. cpcore now re-derives the answer from the step AND the attempt it
      // points at, and publishes it as `gates`.
      try {
        const { journey, gates } = await fetchJourneyWithGates();
        setJourneyStatus(journey?.status);

        const verdict = evaluateGate(gates, "congrats");
        if (verdict.decision === GATE_DECISION.REDIRECT) {
          console.warn(
            "Congrats withheld by the server gate:",
            verdict.reason,
            "->",
            verdict.fallbackStep
          );
          navigate(
            `/b/${userIdentifier}?partner=${partner}&step=${verdict.fallbackStep}`,
            { replace: true }
          );
          return;
        }

        setVerifyingBanking(false);
      } catch (error) {
        console.error("Error verifying banking status for congrats", error);
        // Fail open ONLY on a transport failure. This is deliberate and narrow: the server is
        // the thing that withholds the screen, and it does so by returning
        // `allowed.congrats: false` — a decision we have already acted on above. Reaching here
        // means we got no answer at all (network down, cpcore 5xx), and trapping every tenant
        // on a blank screen during an outage would be a far worse failure than the one this
        // gate exists to prevent.
        setVerifyingBanking(false);
      }
    };

    if (!["xu"].includes(userIdentifier)) startOnboardingJourney();
    else setVerifyingBanking(false);
  }, []);

  useEffect(() => {
    if (journeyStatus === "UNLOCKED" && blockPartners.includes(partner)) {
      const nextStep =
        partnerNavigationRules?.[partner]?.congrats || "/onboarding";
      navigate(`/b/${userIdentifier}?partner=${partner}&step=${nextStep}`);
    }
  }, [journeyStatus]);

  // KOTS is being redirected by the effect above — render nothing to avoid a
  // flash of the congrats screen.
  if (partner === "kots") return null;

  // Do not render congratulations until banking success is verified above; otherwise
  // it can flash before we redirect a not-yet-successful tenant away (#15).
  if (verifyingBanking) return null;

  return (
    <Div100vh
      className={`relative flex flex-col justify-between py-5 object-cover bg-no-repeat bg-cover]`}
    >
      <div className="z-0 absolute mx-auto bottom-20">
        <div className="absolute bg-gradient-to-b from-black to-black/5 w-full h-[20%]" />

        <img
          src={cGif}
          alt="Animated Gif"
          className="w-full aspect-square " // Adjust width and height as needed
        />
        <div className="absolute bottom-0 bg-gradient-to-t from-black to-black/5 w-full h-[20%]" />
      </div>
      <div className="z-10 p-6 flex flex-col justify-between items-center h-full w-full">
        <div className="flex flex-col gap-4 items-center justify-center  sm:h-[50%] pb-10">
          <div className="flex flex-col items-center">
            <span className="text-xl font-normal bg-gradient-to-r from-[#888888] via-[#FFFFFF] to-[#888888] bg-clip-text text-transparent">
              CONGRATULATIONS
            </span>
            <hr className="w-full h-[1px] border-0 bg-gradient-to-r from-[#010101] via-[#FFFFFF] to-[#010101] my-2 z-[9999]" />
          </div>

          <div className="flex flex-col gap-1 items-center">
            {PARTNERS_WITHOUT_ZERO_DEPOSIT_MESSAGING.includes(partner) ? (
              <>
                <span className="font-light text-lg">You are eligible for</span>
                <span className="font-light text-lg">move-in</span>
              </>
            ) : (
              <>
                <span className="font-light text-lg">You are eligible for</span>
                <span className={`text-2xl font-semibold ${theme?.glowColor}`}>
                  ZERO DEPOSIT
                </span>
                <span className="font-light text-lg">move-in</span>
              </>
            )}
          </div>
        </div>

        {allowButton && !blockPartners.includes(partner) ? (
          <PrimaryButton text={"Done"} handleClick={handleClick} />
        ) : (
          <div
            className={`${theme?.tint} rounded-xl border border-purple-200 p-4 sm:p-6 my-4 flex flex-col gap-4`}
          >
            <span className="text-center">
              <span className={`text-purple-500 text-sm `}>Contact</span>
              <span className={`text-purple-500 font-bold`}>
                {" "}
                {partner.toUpperCase()}{" "}
              </span>
              <span className={`text-purple-500 text-sm `}>
                team for further steps
              </span>
            </span>
            <PrimaryButton
              text="Call CirclePe Agent"
              handleClick={() => {
                window.location.href = SUPPORT_TEL_HREF;
              }}
            />
          </div>
        )}
      </div>
    </Div100vh>
  );
}
